import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { useAuth } from './useAuth';
import { useProfile } from './useProfile';

/**
 * Gate for every route behind sign-in.
 *
 * A deactivated or archived account gets an explanation rather than a redirect loop:
 * `profiles_select_self` deliberately has no is_active gate so this screen can read
 * the row and say what is wrong.
 */
export function RequireAuth() {
  const { session, initialising, signOut } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const { data: profile, isPending, isError } = useProfile();

  if (initialising) return <FullPageSpinner />;

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (isPending) return <FullPageSpinner />;

  if (isError || !profile) {
    return (
      <AuthNotice>
        <Alert tone="error">{t('auth.profileMissing')}</Alert>
        <Button variant="secondary" onClick={() => void signOut()}>
          {t('auth.signOut')}
        </Button>
      </AuthNotice>
    );
  }

  if (!profile.is_active || profile.status === 'archived') {
    return (
      <AuthNotice>
        <Alert tone="error">{t('auth.accountInactive')}</Alert>
        <Button variant="secondary" onClick={() => void signOut()}>
          {t('auth.signOut')}
        </Button>
      </AuthNotice>
    );
  }

  return <Outlet />;
}

function AuthNotice({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <div className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        {children}
      </div>
    </main>
  );
}
